import {useCurrentShowId} from "hooks";
import React, {useEffect, useMemo, useState} from "react";
import {useForm} from "react-hook-form";
import {Box} from "@material-ui/core";
import LockIcon from "@material-ui/icons/Lock";
import MenuBookOutlinedIcon from "@material-ui/icons/MenuBookOutlined";
import Form, {ActionButton, Actions, BasicInput} from "view/components/Form";
import FfairModal from "view/components/modal/FfairModal";
import {apiV2} from "api/firebase";
import {useQueryClient} from "@tanstack/react-query";
import {
  ICategory,
  IGlossary,
  TranslatableEntity,
} from "@evennt-tech/api-v2-core";
import Alert from "view/components/Alert";
import {trimValidationPattern} from "tools/validations";
import {useLocalisation} from "hooks/translation";
import {LocalisationTarget} from "ts/app/translation";
import {
  LocalisationProvider,
  LocalisationSection,
} from "view/components/Localisation";
import {isLinkedCategory} from "api/showInfoLibrary/types";
import {isCategoryTitleTaken} from "./categoryTitleConflict";
import {useLibraryLinkStyles} from "./styles";

type Props = {
  isOpen: boolean;
  glossary?: IGlossary | null;
  categories?: ICategory[] | null;
  onClose: () => void;
  oldCategory?: ICategory;
  setSelectedCategory?: (agr0: ICategory) => void;
};

const GlossaryCategoryModal: React.FC<Props> = ({
  isOpen,
  onClose,
  categories,
  oldCategory,
  glossary,
  setSelectedCategory,
}) => {
  const form = useForm<ICategory>({
    mode: "all",
    defaultValues: oldCategory,
  });
  const [categoryError, setCategoryError] = useState(false);
  const {reset} = form;
  const showId = useCurrentShowId();
  const libraryClasses = useLibraryLinkStyles();

  /*
   * `title` is a linked category's only master-owned field, so on one it is the
   * single thing this form cannot change (AC 4) — the server rejects it too.
   * Unlinking is offered from the category toolbar, not from here, because it
   * acts on the category rather than on this form's values.
   */
  const isLinked = isLinkedCategory(oldCategory);

  const queryClient = useQueryClient();

  useEffect(() => {
    reset(oldCategory);
  }, [reset, oldCategory]);

  const validateTitleNotUsed = (title: string): boolean => {
    if (!title) return false;

    if (!glossary) return true;

    if (!categories) return true;

    if (!categories.length) return true;

    const sameTitle = categories.filter((category) => {
      if (oldCategory) {
        return (
          category.title.toLowerCase() === title.trim().toLowerCase() &&
          category.id !== oldCategory.id
        );
      }

      return category.title.toLowerCase() === title.trim().toLowerCase();
    });
    setCategoryError(sameTitle.length >= 1);

    return sameTitle.length === 0;
  };

  const localisationTargets = useMemo<LocalisationTarget[]>(
    () => [
      {
        key: "title",
        entity: TranslatableEntity.CATEGORY,
        entityId: oldCategory?.id,
        field: "title",
        label: "Title",
        maxLength: 84,
        getSourceText: () => form.getValues("title") ?? "",
      },
    ],
    [oldCategory?.id, form]
  );

  const localisation = useLocalisation({
    showId,
    targets: localisationTargets,
    enabled: isOpen,
  });

  const saveCategory = async (newCategory: ICategory) => {
    if (!oldCategory) {
      if (!glossary) {
        throw new Error("Now found glossary");
      }

      const response = await apiV2.glossary.category.create({
        glossaryId: glossary.id,
        title: newCategory.title,
        showId,
      });

      await localisation.save({resolveEntityId: () => response.id});

      setSelectedCategory && setSelectedCategory(response);
      onClose();

      return response;
    }

    await apiV2.glossary.category.update({
      title: newCategory.title,
      categoryId: oldCategory.id,
      showId,
    });

    await localisation.save();

    setSelectedCategory && setSelectedCategory(newCategory);
    onClose();
  };

  const createOrEditCategory = async (newCategory: ICategory) => {
    try {
      return await saveCategory(newCategory);
    } catch (error) {
      if (!isCategoryTitleTaken(error)) throw error;

      setCategoryError(true);
    }
  };

  useEffect(() => {
    form.register("title");
  }, [form]);

  useEffect(() => {
    setCategoryError(false);
  }, [isOpen]);

  return (
    <FfairModal onClose={onClose} open={isOpen} reset={form.reset}>
      <Form
        onSubmit={form.handleSubmit(createOrEditCategory)}
        form={form}
        style={{width: "640px"}}
        title={`${oldCategory ? "Edit" : "Add"} category`}
      >
        <LocalisationProvider controller={localisation}>
          {isLinked && (
            <Alert
              severity="info"
              fullWidth
              className={`${libraryClasses.banner} ${libraryClasses.bannerGap}`}
              customIcon={
                <MenuBookOutlinedIcon className={libraryClasses.bannerIcon} />
              }
            >
              <b>This category is linked to the library.</b> Its name is managed
              there. Its sections keep their own links either way.
            </Alert>
          )}

          <BasicInput
            disabled={isLinked}
            {...(isLinked && {
              InputProps: {
                readOnly: true,
                endAdornment: <LockIcon className={libraryClasses.lockIcon} />,
              },
            })}
            rules={{
              required: true,
              minLength: 1,
              maxLength: 84,
              pattern: trimValidationPattern,
              ...(!isLinked && {validate: validateTitleNotUsed}),
            }}
            onBlur={() => {
              setCategoryError(false);
            }}
            id="title"
            label="Title"
          />

          {categoryError && (
            <Alert severity="error">
              The entered title already exists. Please check your entry.
            </Alert>
          )}

          <LocalisationSection
            id="glossary-category-title"
            targetKeys={["title"]}
            disableBottomMargin
            readOnly={isLinked}
          />

          <Actions>
            <ActionButton
              onClick={onClose}
              text="Cancel"
              name="cy-button-cancel-glossary-category-form"
            />
            <ActionButton
              type="submit"
              disabled={categoryError}
              text={oldCategory ? "Save" : "Add"}
              name="cy-button-add-glossary-category"
              allowClean={localisation.isDirty}
            />
          </Actions>
        </LocalisationProvider>
      </Form>
    </FfairModal>
  );
};

export default GlossaryCategoryModal;
